import { useCallback, useMemo, useState } from 'react';
import { usePagination } from './usePagination';
import { useDebounce } from './useDebounce';
import { clean } from '@/lib/utils';

// One hook to drive a data table: sort + filters + search + pagination.
export const useTableState = ({
  initialSort = null,
  initialFilters = {},
  initialPageSize,
} = {}) => {
  const pagination = usePagination({ initialPageSize });
  const [sort, setSort] = useState(initialSort); // { field, direction: 'asc' | 'desc' }
  const [filters, setFilters] = useState(initialFilters);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  const setFilter = useCallback(
    (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      pagination.setPage(1);
    },
    [pagination],
  );

  const toggleSort = useCallback((field) => {
    setSort((prev) =>
      prev?.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' },
    );
  }, []);

  const queryParams = useMemo(
    () =>
      clean({
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy: sort?.field,
        sortDir: sort?.direction,
        search: debouncedSearch,
        ...filters,
      }),
    [pagination.page, pagination.pageSize, sort, debouncedSearch, filters],
  );

  const hasActiveFilters = useMemo(() => Object.keys(clean(filters)).length > 0, [filters]);

  return {
    ...pagination,
    sort,
    setSort,
    toggleSort,
    filters,
    setFilter,
    setFilters,
    hasActiveFilters,
    search,
    setSearch,
    queryParams,
  };
};
